import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Link } from '~/components/Link';
import styled, { css } from 'styled-components';

import toasts from '~/state/toasts';

import TEST_ID from './index.testid';
import Icon from '../Icon';
import { Body } from '../Typography/index';

const AUTO_CLEAR_TIMEOUT = 10000;

export type LevelType = 'danger' | 'info' | 'success' | 'warning';

export type LinkObj = {
  label: string;
  to: string;
  onClick?: () => void;
};

type Props = {
  /** A unique ID */
  id?: string;

  /** The message */
  message?: string;

  /** Show Upload button or not */
  level?: LevelType;

  /** Optional link to show */
  link?: LinkObj | null;

  /** Used to hide the close button */
  dismissable?: boolean;
};

const iconMap = {
  danger: <Icon name="close" strokeWidth={2.5} />,
  info: <Icon name="info" strokeWidth={2.5} />,
  success: <Icon name="check" strokeWidth={2.5} />,
  warning: <Icon name="exclamation" strokeWidth={2.5} />,
};

const Toast = ({
  id,
  message,
  level = 'info',
  link = null,
  dismissable = true,
}: Props) => {
  const [allToasts, setToasts] = useRecoilState(toasts);

  const clearToast = () => {
    setToasts(allToasts.filter(({ id: toastId }) => id !== toastId));
  };

  useEffect(() => {
    const timeout = setTimeout(clearToast, AUTO_CLEAR_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container data-testid={TEST_ID.CONTAINER} data-messagelevel={level}>
      {dismissable && (
        <CloseContainer onClick={clearToast} data-testid={TEST_ID.CLOSE_BUTTON}>
          <Icon name="close" strokeWidth={2.5} />
        </CloseContainer>
      )}
      <IconContainer level={level}>{iconMap[level]}</IconContainer>
      <Inner>
        <Body
          data-testid={TEST_ID.TOAST_MESSAGE}
          withoutMargin
          size="base"
          color={{ group: 'white' }}
        >
          {message}
        </Body>
        <LinkContainer>
          {link && (
            <Link
              to={link.to}
              onClick={link.onClick}
              data-testid={TEST_ID.LINK}
            >
              {link.label}
            </Link>
          )}
        </LinkContainer>
      </Inner>
    </Container>
  );
};

const Container = styled.div<{}>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  z-index: 500;
  ${({ theme }) => css`
    background-color: ${theme.color('primary')};
    color: ${theme.color('white')};
    padding: ${theme.space('m')} ${theme.space('l')} ${theme.space('m')}
      ${theme.space('m')};
    margin-bottom: ${theme.space('m')};
    border-radius: ${theme.getTokens().border.radius.s};
  `}
`;

const CloseContainer = styled.button<{}>`
  background: transparent;
  border: none;
  outline: none;
  position: absolute;
  cursor: pointer;
  transition: all 350ms ease-out;
  ${({ theme }) => css`
    font-size: ${theme.fontSize('s')};
  `}

  &:hover {
    & svg {
      transform: rotate(180deg);
    }
  }

  ${({ theme }) => css`
    /* Top and right take care of half the position */
    top: ${theme.space('xxs')};
    right: ${theme.space('xxs')};
    /* Padding does other half of position and increases pointer area */
    padding: ${theme.space('xxs')};
  `}
`;

type IconContainerProps = {
  level: LevelType;
};

const IconContainer = styled.span<IconContainerProps>(
  ({ theme, level }) => css`
    margin-right: ${theme.space('l')};
    width: ${theme.space('xxl')};
    height: ${theme.space('xxl')};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    flex-grow: 0;
    border-radius: ${theme.getTokens().border.radius.full};
    background-color: ${theme.color(level)};
    font-size: ${theme.space('m')};
  `,
);

const Inner = styled.div<{}>`
  p {
    margin: 0;
  }
`;

const LinkContainer = styled.div<{}>`
  ${({ theme }) => css`
    a {
      color: ${theme.color('white')};
      font-weight: 100;
    }
    a:hover {
      color: ${theme.color('text', 'light')};
    }
  `}
`;

export default Toast;
